import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'

import { cn } from '@/lib/cn'

type IconButtonVariant = 'ghost' | 'secondary'
type IconButtonSize = 'sm' | 'md'

const variantClassName: Record<IconButtonVariant, string> = {
  ghost: 'bg-transparent text-[var(--color-text)] hover:bg-[var(--color-hover-muted)]',
  secondary:
    'border border-[var(--color-border)] bg-white text-[var(--color-text)] hover:bg-[var(--color-surface-muted)]',
}

const sizeClassName: Record<IconButtonSize, string> = {
  sm: 'size-8',
  md: 'size-10',
}

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  'aria-label': string
  children: ReactNode
  size?: IconButtonSize
  variant?: IconButtonVariant
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { children, className, size = 'md', type = 'button', variant = 'ghost', ...props },
  ref,
) {
  return (
    <button
      className={cn(
        'inline-flex cursor-pointer items-center justify-center rounded-md transition-colors',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]',
        'disabled:cursor-not-allowed disabled:opacity-60',
        variantClassName[variant],
        sizeClassName[size],
        className,
      )}
      ref={ref}
      type={type}
      {...props}
    >
      {children}
    </button>
  )
})
